"use client"

import { FragmentType, graphql, useFragment } from "@/gql"
import { PortfolioSearchParams, PortfolioUIState } from "../../page"
import { EditLocationModal } from "./EditLocationModal"
import { EditNameModal } from "./EditNameModal"
import { EditRolesModal } from "./EditRolesModal"
import { ReorderProjectsModal } from "./ReorderProjectModal"
import { PortfolioEditModal, useEditPortfolio } from "./useEditPortfolio"
import { DeleteProjectModal } from "./DeleteProjectModal"
import { EditDescriptionModal } from "./EditDescriptionModal"
import { EditSkillsModal } from "./EditSkillsModal"
import { EditProjectTypesModal } from "./EditProjectTypesModal"
import { EditLanguagesModal } from "./EditLanguagesModal"
import { EditContactInfoModal } from "./EditContactInfoModal"
import { useEffect } from "react"
import { ProfileVisibilityModal } from "./ProfileVisibilityModal"
import { EditProfileImageModal } from "@/components/EditProfileImageModal"

const EditModalsFragment = graphql(`
  fragment EditModals on IndividualCreative {
    id
    __typename
    handle

    ...EditNameModal
    ...EditLocationModal
    ...EditRolesModal
    ...EditDescriptionModal
    ...EditSkillsModal
    ...EditLanguagesModal
    ...EditProjectTypesModal
    ...EditContactInfoModal
    ...ProfileVisibilityModal

    user {
      id
      ...EditProfileImageModal
    }
  }
`)

interface Props {
  uiState: PortfolioUIState
  creative: FragmentType<typeof EditModalsFragment> | null | undefined
  searchParams: PortfolioSearchParams
  editProfileMethods?: ReturnType<typeof useEditPortfolio>
}

export const EditModals: React.FC<Props> = ({ uiState, editProfileMethods, searchParams, ...props }) => {
  const creative = useFragment(EditModalsFragment, props.creative)

  const onOpenChange = (isOpen: boolean, modal: PortfolioEditModal) => {
    if (isOpen) {
      editProfileMethods?.setOpenModal(modal)
    } else {
      editProfileMethods?.setOpenModal(null)
    }
  }

  useEffect(() => {
    // This param us used when we redirect back to the page after adding a social contact
    if (searchParams.openContactModal === "true") {
      editProfileMethods?.setOpenModal(PortfolioEditModal.EditContactInfo)

      // Remove the query param
      const newSearchParams = new URLSearchParams(window.location.search)
      newSearchParams.delete("openContactModal")
      window.history.replaceState({}, "", `${window.location.pathname}?${newSearchParams}`)
    }
  }, [searchParams, editProfileMethods?.setOpenModal])

  return (
    <>
      <EditLocationModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.EditLocation}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.EditLocation)}
      />
      <EditDescriptionModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.EditDescription}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.EditDescription)}
      />
      <EditNameModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.EditName}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.EditName)}
      />
      <EditRolesModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.EditRoles}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.EditRoles)}
      />
      <EditLanguagesModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.EditLanguages}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.EditLanguages)}
      />
      <EditProjectTypesModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.EditProjecTypes}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.EditProjecTypes)}
      />
      <EditSkillsModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.EditSkills}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.EditSkills)}
      />
      <EditContactInfoModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.EditContactInfo}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.EditContactInfo)}
      />
      <EditProfileImageModal
        user={creative?.user}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.UpdateAvatarImage}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.UpdateAvatarImage)}
      />
      <ProfileVisibilityModal
        creative={creative}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.ProfileVisibility}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.ProfileVisibility)}
      />
      <ReorderProjectsModal
        creativeHandle={creative?.handle!}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.ReorderProjects}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.ReorderProjects)}
      />
      <DeleteProjectModal
        editProfileMethods={editProfileMethods}
        isOpen={editProfileMethods?.openModal === PortfolioEditModal.DeleteProject}
        onOpenChange={open => onOpenChange(open, PortfolioEditModal.DeleteProject)}
      />
    </>
  )
}
